import type { ChangeMapper, ValueAccessor } from '../components/DesignSystemComponentRenderer';
import type { ResolvedRenderer } from './resolveRenderer';

function isMultiFieldDirective(v: ValueAccessor | ChangeMapper | undefined): boolean {
  return !!v && typeof v === 'object' && v.type === 'multiField';
}

/**
 * True when a resolved legacy renderer (`editComponent`/`displayComponent`) carries a
 * `{type:'multiField'}` `changeMapper`/`valueAccessor` — the caller must then pass the WHOLE
 * Formik `values` bag (not the single property value) as `UnifiedPropertyRendererMolecule`'s `value`,
 * mirroring `AdditionalPropertiesGrid`'s pre-Phase-4 `renderCustomComponent` (EAINT-855 gap 5).
 *
 * This governs the READ side only (what `value` to pass down) — `DesignSystemComponentRenderer`
 * needs the whole bag to resolve either directive's form-field paths. It does NOT tell the caller
 * whether to fan an incoming change out to multiple paths; that is `isMultiFieldChangeMapper`,
 * below (EAINT-855 review pass 2, L-10).
 */
export function isMultiFieldLegacy(resolved: ResolvedRenderer): boolean {
  const shim = resolved.legacyShim;
  return isMultiFieldDirective(shim?.changeMapper) || isMultiFieldDirective(shim?.valueAccessor);
}

/**
 * True only when the resolved legacy shim's `changeMapper` (not `valueAccessor`) is
 * `{type:'multiField'}` — the WRITE-side check. `DesignSystemComponentRenderer.handleChange` only
 * fans a change out across `mappings` when `changeMapper` itself is multiField; a multiField
 * `valueAccessor` with no `changeMapper` still emits its child's payload as-is (see
 * `DesignSystemComponentRenderer`'s "If no changeMapper, use childPayload as-is" branch). Callers
 * that fan out on `isMultiFieldLegacy` (which is also true for a valueAccessor-only shim) call
 * `fanOutMultiFieldChange` with an undefined `changeMapper`, which no-ops and silently drops the
 * edit — this predicate is what the write-decision must use instead (EAINT-855 review pass 2, L-10).
 */
export function isMultiFieldChangeMapper(resolved: ResolvedRenderer): boolean {
  return isMultiFieldDirective(resolved.legacyShim?.changeMapper);
}

function isObjectValue(v: unknown): boolean {
  return v !== null && typeof v === 'object';
}

/** Mirrors `safeAccess` in `AdditionalPropertiesGrid.tsx`/`SphereEditor.tsx` — dot-path read. */
export function safeAccessPath(obj: any, path: string): any {
  if (!path) return obj;
  return path.split('.').reduce((o, key) => (o != null ? o[key] : undefined), obj);
}

/**
 * Fans a multi-field legacy component's combined payload out to individual Formik fields via
 * `setFieldValue`, one call per mapping — exactly `AdditionalPropertiesGrid.handleComponentChange`
 * / `SphereEditor`'s docked-tab inline equivalent, extracted so both call sites (and any future
 * one) share one implementation (EAINT-855 SC5).
 *
 * `newValue` here is `DesignSystemComponentRenderer`'s ALREADY-mapped `onChange` payload — for a
 * multi-field `changeMapper` that component deep-clones the whole values bag it was given and
 * writes each mapping's `payloadPath` value at its `formFieldPath` key BEFORE calling `onChange`
 * (see `DesignSystemComponentRenderer.handleChange`). So `newValue` is keyed by `formFieldPath`
 * (e.g. `newValue.properties.startDate`), not by the mapper's `payloadPath` — this fan-out reads
 * it back out at each `formFieldPath` and re-applies only the ones that actually changed.
 */
export function fanOutMultiFieldChange(
  newValue: unknown,
  changeMapper: ChangeMapper | undefined,
  currentValues: unknown,
  setFieldValue: (path: string, value: unknown) => void
): void {
  if (!changeMapper || typeof changeMapper !== 'object' || changeMapper.type !== 'multiField') return;
  Object.keys(changeMapper.mappings).forEach((formFieldPath) => {
    const fieldValue = safeAccessPath(newValue, formFieldPath);
    const currentValue = safeAccessPath(currentValues, formFieldPath);
    const changed =
      isObjectValue(fieldValue) || isObjectValue(currentValue)
        ? JSON.stringify(fieldValue) !== JSON.stringify(currentValue)
        : fieldValue !== currentValue;
    if (changed) setFieldValue(formFieldPath, fieldValue);
  });
}

/** Mirrors the single-field dedupe-then-set used alongside the multi-field fan-out above. */
export function setIfChanged(fieldName: string, newValue: unknown, currentValue: unknown, setFieldValue: (path: string, value: unknown) => void): void {
  const changed =
    isObjectValue(newValue) || isObjectValue(currentValue) ? JSON.stringify(newValue) !== JSON.stringify(currentValue) : newValue !== currentValue;
  if (changed) setFieldValue(fieldName, newValue);
}
